import type { shell } from 'punica';
import { createStatusDot, isActiveState } from '../statusDot/index.js';

interface StatusbarItem {
  id: string;
  label: string;
  alignment: 'primary' | 'secondary';
  title: string;
  icon: string;
  /**
   * Live state of what the item reflects, drawn as a status dot in front of the
   * icon. Belongs here rather than as a character inside `label` — one glyph,
   * one implementation, and it can animate while it is running.
   */
  status?: shell.View.Helpers.StatusDotState;
  /** Command (or capability) id executed when the item is clicked. */
  command?: string;
  capability?: string;
}

interface IStatusbarManager {
  addItem(item: StatusbarItem): void;
  removeItem(itemId: string): void;
}

class StatusbarManager implements IStatusbarManager {
  #primaryItems: Map<string, StatusbarItem> = new Map();
  #secondaryItems: Map<string, StatusbarItem> = new Map();
  #content: HTMLElement;
  #primaryContent: HTMLElement;
  #secondaryContent: HTMLElement;

  // Persistent, per-id insertion order. Assigned the first time an id is seen
  // (which equals extension load order) and NEVER cleared on removeItem, so an
  // item retracted by unload() and re-added by a reload/update drops back into
  // its original slot instead of jumping to the end of its group.
  #order: Map<string, number> = new Map();
  #nextOrder = 0;

  /**
   * The node rendered for each id.
   *
   * Kept here rather than looked up with `querySelector('#' + id)`, which is
   * how items with a dotted id (`llms.ollama.progress`) became unremovable:
   * that selector parses as id `llms` with classes `ollama progress`, matches
   * nothing, and the node stays in the DOM forever while `removeItem` reports
   * nothing to do. Observed as spinners that accumulated in the status bar and
   * never stopped. An id is an identifier, not a selector.
   */
  #nodes: Map<string, HTMLElement> = new Map();

  /**
   * Insert `button` into `container` at the position dictated by its persistent
   * order index, replacing any existing node with the same id in place. New ids
   * get the next order value and land at the end of the group.
   */
  #insertOrdered(container: HTMLElement, button: HTMLElement, id: string): void {
    if (!this.#order.has(id)) {
      this.#order.set(id, this.#nextOrder++);
    }
    const order = this.#order.get(id) as number;

    // Drop any existing node for this id so a re-register never duplicates.
    const existing = this.#nodes.get(id);
    if (existing?.parentNode) {
      existing.parentNode.removeChild(existing);
    }
    this.#nodes.set(id, button);

    // Insert before the first sibling whose order is greater than ours.
    let ref: Element | null = null;
    for (const child of Array.from(container.children)) {
      const childOrder = this.#order.get(child.id);
      if (childOrder !== undefined && childOrder > order) {
        ref = child;
        break;
      }
    }
    container.insertBefore(button, ref);
  }

  /**
   *
   */
  #initiliazeContent() {
    // Use the shell layout manager exposed via `punica.shell.Layout`.
    const layoutManager = (window as any).punica?.shell?.Layout.manager;
    const statusbarSlot = layoutManager.getSlot('statusbar');
    const statusbarContent = statusbarSlot?.content;

    // Plain flex containers (styled by the host theme) instead of the
    // punica-row/col web components: statusbar items must lay out
    // horizontally at a fixed, compact height, so we keep full control of
    // the layout via `.status-bar-*` classes rather than shadow-DOM defaults.
    this.#content = document.createElement('div');
    this.#content.className = 'status-bar-inner';

    this.#primaryContent = document.createElement('div');
    this.#primaryContent.className =
      'status-bar-group status-bar-group-primary';

    this.#secondaryContent = document.createElement('div');
    this.#secondaryContent.className =
      'status-bar-group status-bar-group-secondary';

    this.#content.appendChild(this.#primaryContent);
    this.#content.appendChild(this.#secondaryContent);

    statusbarContent?.appendChild(this.#content);
  }

  /**
   *
   */
  constructor() {}

  /**
   *
   * @param alignment
   * @param item
   */
  addItem(item: StatusbarItem): void {
    if (!this.#content) {
      this.#initiliazeContent();
    }

    const button = document.createElement('button');

    button.type = 'button';
    button.id = item.id;
    button.title = item.title;
    button.className = 'status-bar-action';

    // Wire the click to the item's command / capability (if any). Extensions
    // that need custom click behavior can still overwrite `onclick` after
    // addItem() returns (e.g. the notifications extension).
    const commandId = item.command ?? item.capability;
    button.onclick = () => {
      if (!commandId) return;
      try {
        punica.kernel.Commands.manager.executeCommand(commandId);
      } catch (error) {
        console.error(
          '[Statusbar] Failed to execute command for item',
          item.id,
          commandId,
          error
        );
      }
    };

    const parts: string[] = [];
    if (item.icon) {
      parts.push(`<punica-icon size="small">${item.icon}</punica-icon>`);
    }
    if (item.label) {
      parts.push(`<span class="status-bar-label">${item.label}</span>`);
    }
    button.innerHTML = parts.join('');

    // Leftmost, ahead of the icon — same reading order as a sidebar card. Only
    // states that mean work is happening right now animate; the rest are flat
    // colour. `.status-bar-action` is already a flex row with a gap, so the dot
    // needs no spacing of its own.
    if (item.status) {
      button.prepend(
        createStatusDot(item.status, {
          pulse: isActiveState(item.status),
          className: 'status-bar-dot'
        })
      );
    }

    const isPrimary = item.alignment === 'primary';
    const targetContent = isPrimary
      ? this.#primaryContent
      : this.#secondaryContent;
    const items = isPrimary ? this.#primaryItems : this.#secondaryItems;

    items.set(item.id, item);

    // Position-stable insert: replaces any existing node in place and, on a
    // reload/update, re-inserts at the item's persistent order slot — so the
    // item is never duplicated and never jumps to the end of its group.
    this.#insertOrdered(targetContent, button, item.id);
  }

  /**
   *
   * @param itemId
   */
  removeItem(itemId: string): void {
    // The node first, then the bookkeeping, and both unconditionally: an item
    // that has lost its element must still leave the registry, or it can never
    // be re-added in the right place either.
    const node = this.#nodes.get(itemId);
    if (node?.parentNode) {
      node.parentNode.removeChild(node);
    }
    this.#nodes.delete(itemId);
    this.#primaryItems.delete(itemId);
    this.#secondaryItems.delete(itemId);
  }
}

type StatusbarNamespace = {
  manager: IStatusbarManager;
};

const statusbar: StatusbarNamespace = {
  manager: new StatusbarManager()
};

export default statusbar;
